import React, { ReactElement, useEffect, useState } from 'react';
import FriendlyTimestamp from '../../../types/FriendlyTimestamp';
import AppChart from '../../AppChart';
import CcxComponentProps from '../../../core/CcxComponent';
import ChartCustomTooltip from './ChartCustomTooltip';
import PrometheusMySqlDbConnectionsStats from '../../../types/PrometheusMySqlDbConnectionsStats';
import Deployment from '../../../types/Deployment';

interface Props extends CcxComponentProps {
    data?: PrometheusMySqlDbConnectionsStats[];
    loading: boolean;
    displaySummary?: boolean;
    height?: number;
    width?: number;
    hostUUIDs?: string[];
    deployment?: Deployment;
}

function PrometheusMySqlDbConnectionsChart({
    data,
    loading,
    displaySummary,
    testId = 'PrometheusMySqlDbConnectionsChart',
    width,
    height = 250,
    hostUUIDs,
    deployment,
}: Props): ReactElement {
    const [chartData, setChartData] = useState<any>([]);
    const [summary, setSummary] = useState<any>([]);

    const setChartSummary = (data: any, series: any) => {
        const max: number[] = [];
        const min: number[] = [];
        const sum: number[] = [];
        const avg: number[] = [];

        series.forEach((s: any) => {
            max[s.value] = 0;
            min[s.value] = 0;
            sum[s.value] = 0;
            avg[s.value] = 0;
        });

        data.forEach((d: any) => {
            series.forEach((s: any) => {
                sum[s.value] += d[s.value];

                min[s.value] =
                    d[s.value] < min[s.value] ? d[s.value] : min[s.value];
                max[s.value] =
                    d[s.value] > max[s.value] ? d[s.value] : max[s.value];
            });
        });

        series.forEach((s: any) => {
            avg[s.value] = sum[s.value] / data.length;
        });

        const dataSummary = series.map((s: any) => {
            return {
                serie: s.value,
                label: s.label,
                avg: avg[s.value].toFixed(2),
                min: min[s.value].toFixed(2),
                max: max[s.value].toFixed(2),
                sum: sum[s.value].toFixed(2),
            };
        });

        setSummary(dataSummary);
    };

    useEffect(() => {
        if (
            data &&
            data.length > 0 &&
            (deployment || !(deployment && hostUUIDs))
        ) {
            const formattedData = data.map(
                (stat: PrometheusMySqlDbConnectionsStats) => {
                    return [
                        {
                            yValue: stat.threadConnected,
                            series: hostUUIDs
                                ? deployment?.getHostName(stat.instance)
                                : 'thread_connected',
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                        !hostUUIDs && {
                            yValue: stat.maxUsedConnections,
                            series: 'max_used_connections',
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                        !hostUUIDs && {
                            yValue: stat.maxConnections,
                            series: 'max_connections',
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                        !hostUUIDs && {
                            yValue: stat.abortedClients,
                            series: 'aborted_clients',
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                        !hostUUIDs && {
                            yValue: stat.abortedConnects,
                            series: 'aborted_connects',
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                        !hostUUIDs && {
                            yValue: stat.connections,
                            series: 'connections',
                            xValue: new FriendlyTimestamp(
                                stat.created
                            ).getDate(),
                        },
                    ];
                }
            );
            setChartData(formattedData.flat());

            const summarySeries = [
                {
                    value: 'threadConnected',
                    label: 'thread_connected',
                },
                {
                    value: 'maxUsedConnections',
                    label: 'max_used_connections',
                },
                {
                    value: 'maxConnections',
                    label: 'max_connections',
                },
                {
                    value: 'abortedClients',
                    label: 'aborted_clients',
                },
                {
                    value: 'abortedConnects',
                    label: 'aborted_connects',
                },
                {
                    value: 'connections',
                    label: 'connections',
                },
            ];
            setChartSummary(data, summarySeries);
        }
    }, [data, hostUUIDs, deployment]);

    const chartConfig = {
        title: {
            visible: true,
            text: 'Database connections',
        },
        description: {
            visible: true,
            text: '',
        },
        height,
        width,
        autoFit: !width && !height,
        xField: 'xValue',
        yField: 'yValue',
        xAxis: {
            type: 'time',
            mask: 'HH:mm',
        },
        animation: false,
        seriesField: 'series',
        tooltip: {
            customContent: (title: any, data: any) => {
                const getTitle = () => {
                    if (!data || data.length === 0) return '';
                    return data[0]?.data?.xValue.toString();
                };

                const getContent = () => {
                    if (!data || data.length === 0) return [];
                    return data.map((d: any) => ({
                        color: d?.color,
                        label: d?.data?.series,
                        value: d?.data?.yValue,
                    }));
                };

                const tooltipData = {
                    title: getTitle(),
                    content: getContent(),
                };

                return (
                    <ChartCustomTooltip
                        title={tooltipData.title}
                        content={tooltipData.content}
                    />
                );
            },
        },
    };

    return (
        <AppChart
            type="area"
            data={chartData}
            config={chartConfig}
            summary={summary}
            loading={loading}
            displaySummary={displaySummary}
            testId={testId}
        />
    );
}

export default PrometheusMySqlDbConnectionsChart;
